import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Images, Palette, BarChart3, LogOut, Trash2, Star, Plus, Loader2, Coins, DollarSign, ImageIcon, Layers, KeyRound, ShieldCheck, Copy, Check, Lock, Paintbrush, Upload, TrendingUp, RefreshCw, ExternalLink, Zap, ChevronUp, ChevronDown, FileText,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { TIER_NAMES, useEntitlements, useBranding } from '../lib/entitlements';
import { DEFAULT_OFFERS, toEditableOffers } from '../lib/offers';
import { downscaleToDataUrl } from '../lib/img';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';

const TABS = [
  { key: 'overview', label: 'Overview', Icon: BarChart3 },
  { key: 'sales', label: 'Sales & Affiliates', Icon: TrendingUp },
  { key: 'users', label: 'Users', Icon: Users },
  { key: 'license', label: 'License & Modules', Icon: ShieldCheck },
  { key: 'keys', label: 'API Keys', Icon: KeyRound },
  { key: 'branding', label: 'Whitelabel', Icon: Paintbrush },
  // Su propia pestana y no dentro de Whitelabel, que esta bloqueada tras un
  // nivel de pago: estos datos los necesita todo el que venda, no solo quien
  // rebautiza el producto.
  { key: 'legal', label: 'Legal', Icon: FileText },
  { key: 'gallery', label: 'Gallery', Icon: Images },
  { key: 'styles', label: 'Styles', Icon: Palette },
];

const inputCls = 'rounded-md border border-[#2A2A30] bg-[#141417] px-2.5 py-1.5 text-sm text-white focus:border-white/30 focus:outline-none';

// Confirmación reutilizable para acciones destructivas del panel.
function ConfirmAction({ title, description, actionLabel = 'Delete', onConfirm, children }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="border-[#2A2A30] bg-[#141417]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-500 text-white hover:bg-red-600">{actionLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


function Overview() {
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api.adminStats().then(setS).catch((e) => setError(e.message || 'Could not load stats')); }, []);
  if (error) return <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">Could not load stats: {error}</div>;
  if (!s) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;
  const cards = [
    { label: 'Total users', value: s.users, sub: `${s.paid_users} paid`, Icon: Users, color: '#A78BFA' },
    { label: 'Generations', value: s.generations, sub: `${s.generations_succeeded} ok · ${s.generations_failed} failed`, Icon: ImageIcon, color: '#22D3EE' },
    { label: 'Assets', value: s.assets, sub: 'uploaded', Icon: Layers, color: '#F5A623' },
    { label: 'Workflows', value: s.workflows, sub: 'built', Icon: Layers, color: '#34D399' },
    { label: 'Inference cost', value: `$${s.inference_cost_usd}`, sub: 'real Requesty spend', Icon: DollarSign, color: '#34D399' },
    { label: 'Credits charged', value: s.credits_charged, sub: 'across all users', Icon: Coins, color: '#F5A623' },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((c) => (
        <div key={c.label} className="rounded-2xl border border-[#2A2A30] bg-[#141417] p-5">
          <c.Icon size={18} style={{ color: c.color }} />
          <p className="mt-3 text-2xl font-semibold text-white">{c.value}</p>
          <p className="text-sm text-white/70">{c.label}</p>
          <p className="mt-1 text-xs text-white/40">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.adminUsers().then((u) => { setUsers(u); setLoading(false); })
      .catch((e) => { setLoading(false); toast({ title: 'Could not load users', description: e.message }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (id, patch) => {
    try { await api.adminPatchUser(id, patch); toast({ title: 'User updated' }); load(); }
    catch (e) { toast({ title: 'Update failed', description: e.message }); }
  };
  const del = async (id) => {
    try { await api.adminDeleteUser(id); setUsers((p) => p.filter((u) => u.id !== id)); toast({ title: 'User deleted' }); }
    catch (e) { toast({ title: 'Delete failed', description: e.message }); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#2A2A30]">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="border-b border-[#2A2A30] bg-[#141417] text-left text-xs uppercase tracking-wider text-white/50">
          <tr><th className="p-3">User</th><th className="p-3">Role</th><th className="p-3">Plan</th><th className="p-3">Credits</th><th className="p-3"></th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-[#1e1e22]">
              <td className="p-3"><p className="font-medium text-white">{u.name}</p><p className="text-xs text-white/40">{u.email}</p></td>
              <td className="p-3">
                <select defaultValue={u.role || 'user'} onChange={(e) => save(u.id, { role: e.target.value })} className={inputCls}>
                  <option value="user">user</option><option value="admin">admin</option>
                </select>
              </td>
              <td className="p-3">
                <select defaultValue={u.plan || 'free'} onChange={(e) => save(u.id, { plan: e.target.value })} className={inputCls}>
                  {['free', 'hobby', 'pro', 'ultra'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </td>
              <td className="p-3">
                <input type="number" defaultValue={u.credits_balance} className={`${inputCls} w-28`}
                  onBlur={(e) => { const v = parseInt(e.target.value); if (!Number.isNaN(v) && v !== u.credits_balance) save(u.id, { credits_balance: v }); }} />
              </td>
              <td className="p-3 text-right">
                <ConfirmAction title="Delete user?" description={`This permanently removes ${u.email} and their access. This cannot be undone.`} onConfirm={() => del(u.id)}>
                  <button className="text-white/40 hover:text-red-400"><Trash2 size={15} /></button>
                </ConfirmAction>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', vertical: 'fashion', src: '', aspect: 'square' });
  const load = useCallback(() => { api.adminGallery().then(setItems).catch((e) => toast({ title: 'Could not load gallery', description: e.message })); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title || !form.src) { toast({ title: 'Title and image URL required' }); return; }
    try { await api.adminCreateGallery(form); setForm({ title: '', vertical: 'fashion', src: '', aspect: 'square' }); load(); }
    catch (e) { toast({ title: 'Create failed', description: e.message }); }
  };
  const toggle = async (it) => {
    try { await api.adminToggleGallery(it.id, !it.featured); load(); }
    catch (e) { toast({ title: 'Update failed', description: e.message }); }
  };
  const del = async (id) => {
    try { await api.adminDeleteGallery(id); setItems((p) => p.filter((i) => i.id !== id)); toast({ title: 'Gallery item deleted' }); }
    catch (e) { toast({ title: 'Delete failed', description: e.message }); }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end gap-2 rounded-2xl border border-[#2A2A30] bg-[#141417] p-4">
        <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} flex-1`} />
        <input placeholder="Image URL" value={form.src} onChange={(e) => setForm({ ...form, src: e.target.value })} className={`${inputCls} flex-[2]`} />
        <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className={inputCls}>
          {['fashion', 'product', 'beauty', 'editorial'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <button onClick={add} className="inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-medium text-black"><Plus size={14} /> Add</button>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((it) => (
          <div key={it.id} className="overflow-hidden rounded-xl border border-[#2A2A30] bg-[#141417]">
            <div className="aspect-square w-full overflow-hidden bg-[#0E0E10]">
              <img src={it.src} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="p-3">
              <p className="truncate text-sm font-medium text-white">{it.title}</p>
              <p className="text-[11px] uppercase text-white/40">{it.vertical}</p>
              <div className="mt-2 flex items-center justify-between">
                <button onClick={() => toggle(it)} className={`inline-flex items-center gap-1 text-xs ${it.featured ? 'text-node-product' : 'text-white/40'}`}>
                  <Star size={13} fill={it.featured ? '#F5A623' : 'none'} /> {it.featured ? 'Featured' : 'Feature'}
                </button>
                <ConfirmAction title="Delete gallery item?" description={`“${it.title}” will disappear from the public gallery. This cannot be undone.`} onConfirm={() => del(it.id)}>
                  <button className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
                </ConfirmAction>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StylesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ slug: '', name: '', vertical: 'fashion', accent: '#22D3EE', desc: '', prompt_block: '' });
  const load = useCallback(() => { api.adminTemplates().then(setItems).catch((e) => toast({ title: 'Could not load styles', description: e.message })); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.slug || !form.name) { toast({ title: 'Slug and name required' }); return; }
    try { await api.adminCreateTemplate(form); setForm({ slug: '', name: '', vertical: 'fashion', accent: '#22D3EE', desc: '', prompt_block: '' }); load(); }
    catch (e) { toast({ title: 'Create failed', description: e.message }); }
  };
  const del = async (id) => {
    try { await api.adminDeleteTemplate(id); setItems((p) => p.filter((t) => t.id !== id)); }
    catch (e) { toast({ title: 'Delete failed', description: e.message }); }
  };
  const save = async (id, patch) => {
    try { await api.adminPatchTemplate(id, patch); toast({ title: 'Template saved' }); }
    catch (e) { toast({ title: 'Save failed', description: e.message }); }
  };

  return (
    <div>
      <div className="mb-6 grid gap-2 rounded-2xl border border-[#2A2A30] bg-[#141417] p-4 sm:grid-cols-2 lg:grid-cols-6">
        <input placeholder="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} />
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className={inputCls}>
          {['fashion', 'product', 'beauty', 'editorial'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 w-full rounded-md border border-[#2A2A30] bg-[#141417]" />
        <input placeholder="prompt block" value={form.prompt_block} onChange={(e) => setForm({ ...form, prompt_block: e.target.value })} className={`${inputCls} lg:col-span-1`} />
        <button onClick={add} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-medium text-black"><Plus size={14} /> Add</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: t.accent }} />
                <span className="font-medium text-white">{t.name}</span>
              </div>
              <button onClick={() => del(t.id)} className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            <p className="mt-1 text-[11px] uppercase text-white/40">{t.vertical} · {t.slug}</p>
            <textarea defaultValue={t.prompt_block} rows={2} onBlur={(e) => save(t.id, { prompt_block: e.target.value })}
              className="mt-3 w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2 text-xs text-white/80 focus:border-white/30 focus:outline-none" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SalesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    setLoading(true); setError('');
    api.adminSalesSummary().then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e.message || 'Could not load sales data'); setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">
      Could not load sales data: {error}
      <button onClick={load} className="ml-3 inline-flex items-center gap-1 rounded-full border border-red-500/30 px-3 py-1 text-xs text-red-200 hover:text-white"><RefreshCw size={11} /> Retry</button>
    </div>
  );
  if (loading || !data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;

  const { totals, by_product, by_affiliate, recent } = data;
  const empty = (recent || []).length === 0;
  const cur = (n) => `£${Number(n || 0).toFixed(2)}`;
  const productRows = Object.entries(by_product || {}).sort((a, b) => b[1].sales - a[1].sales);
  const affRows = Object.entries(by_affiliate || {}).sort((a, b) => b[1].sales - a[1].sales);

  return (
    <div className="space-y-5" data-testid="sales-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif-display text-xl text-white">Sales &amp; affiliates</h2>
          <p className="mt-0.5 text-xs text-white/45">Auto-tracked from JVZoo &amp; WarriorPlus purchase webhooks.</p>
        </div>
        <button onClick={load} data-testid="sales-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] px-3 py-1.5 text-xs text-white/70 hover:text-white"><RefreshCw size={13} /> Refresh</button>
      </div>

      {empty ? (
        <div className="rounded-xl border border-dashed border-[#2A2A30] bg-[#141417] p-8 text-center">
          <TrendingUp size={28} className="mx-auto text-white/25" />
          <p className="mt-3 text-sm text-white/60">No sales yet.</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-white/40">Once you connect the JVZoo / WarriorPlus webhooks (License &amp; Modules tab) and buyers purchase, every sale, refund and affiliate shows up here automatically.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4" data-testid="sales-total">
              <p className="text-[11px] uppercase tracking-wider text-white/40">Sales</p>
              <p className="mt-1 text-2xl font-semibold text-node-generator">{totals.sales}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
              <p className="text-[11px] uppercase tracking-wider text-white/40">Refunds</p>
              <p className="mt-1 text-2xl font-semibold text-red-300">{totals.refunds}</p>
            </div>
            <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
              <p className="text-[11px] uppercase tracking-wider text-white/40">Revenue</p>
              <p className="mt-1 text-2xl font-semibold text-white">{cur(totals.revenue)}</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
              <h3 className="mb-3 text-sm font-semibold text-white">By product</h3>
              <div className="space-y-1.5">
                {productRows.map(([tier, s]) => (
                  <div key={tier} className="flex items-center justify-between rounded-md bg-[#0E0E10] px-2.5 py-2 text-xs">
                    <span className="text-white/80">{TIER_NAMES[tier] || tier}</span>
                    <span className="flex items-center gap-3 text-white/60"><span className="text-node-generator">{s.sales} sales</span>{s.refunds > 0 && <span className="text-red-300">{s.refunds} ref.</span>}<span className="text-white">{cur(s.revenue)}</span></span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
              <h3 className="mb-3 text-sm font-semibold text-white">By affiliate</h3>
              <div className="space-y-1.5">
                {affRows.map(([aff, s]) => (
                  <div key={aff} className="flex items-center justify-between rounded-md bg-[#0E0E10] px-2.5 py-2 text-xs">
                    <span className="truncate text-white/80">{aff}</span>
                    <span className="flex items-center gap-3 text-white/60"><span className="text-node-generator">{s.sales} sales</span>{s.refunds > 0 && <span className="text-red-300">{s.refunds} ref.</span>}<span className="text-white">{cur(s.revenue)}</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
            <h3 className="mb-3 text-sm font-semibold text-white">Recent activity</h3>
            <div className="space-y-1">
              {recent.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-md bg-[#0E0E10] px-2.5 py-1.5 text-xs">
                  <span className="flex items-center gap-2 truncate">
                    <span className="uppercase text-white/35">{e.source}</span>
                    <span className="text-white/70">{TIER_NAMES[e.tier] || e.tier || 'unmapped'}</span>
                    {e.email && <span className="truncate text-white/35">{e.email}</span>}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {e.affiliate && <span className="text-white/40">{e.affiliate}</span>}
                    <span className={['unlock', 'sale'].includes(e.action) ? 'text-node-generator' : 'text-red-300'}>{e.action}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OffersEditor() {
  const [offers, setOffers] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.adminGetConfig().then((c) => setOffers(toEditableOffers(c.tier_offers))).catch((e) => toast({ title: 'Could not load offer pricing', description: e.message })); }, []);
  if (!offers) return null;

  const upd = (key, field, val) => setOffers((p) => ({ ...p, [key]: { ...p[key], [field]: val } }));
  const save = async () => {
    setSaving(true);
    try { await api.adminSaveConfig({ tier_offers: offers }); toast({ title: 'Pricing & benefits saved' }); }
    catch (e) { toast({ title: 'Save failed', description: e.message }); }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4" data-testid="offers-editor">
      <h3 className="text-sm font-semibold text-white">Offer pricing &amp; benefits</h3>
      <p className="mb-3 text-xs text-white/50">Edit the price, tagline and bullet benefits shown on your landing, pricing page and each upsell sub-landing. One benefit per line.</p>
      <div className="space-y-3">
        {DEFAULT_OFFERS.map((d) => {
          const o = offers[d.key] || { price: '', cadence: '', tagline: '', features: [] };
          return (
            <div key={d.key} className="rounded-lg border border-[#2A2A30] bg-[#0E0E10] p-3" data-testid={`offer-edit-${d.key}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">{d.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-white/30">{d.key}</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <input value={o.price} onChange={(e) => upd(d.key, 'price', e.target.value)} placeholder="$37" data-testid={`offer-price-${d.key}`} className={inputCls} />
                <input value={o.cadence} onChange={(e) => upd(d.key, 'cadence', e.target.value)} placeholder="one-time" data-testid={`offer-cadence-${d.key}`} className={inputCls} />
                <input value={o.tagline} onChange={(e) => upd(d.key, 'tagline', e.target.value)} placeholder="Tagline" data-testid={`offer-tagline-${d.key}`} className={inputCls} />
              </div>
              <textarea value={(o.features || []).join('\n')} onChange={(e) => upd(d.key, 'features', e.target.value.split('\n'))} rows={4} placeholder="One benefit per line" data-testid={`offer-features-${d.key}`}
                className="mt-2 w-full resize-none rounded-md border border-[#2A2A30] bg-[#141417] p-2 text-xs text-white/80 focus:border-white/30 focus:outline-none" />
            </div>
          );
        })}
      </div>
      <button onClick={save} disabled={saving} data-testid="offers-save" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-medium text-black disabled:opacity-50">
        {saving && <Loader2 size={14} className="animate-spin" />} Save pricing &amp; benefits
      </button>
    </div>
  );
}

function LicenseTab() {
  const [cfg, setCfg] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [mintTiers, setMintTiers] = useState(['fe']);
  const [minted, setMinted] = useState('');
  const [copied, setCopied] = useState(false);
  const [buyLinks, setBuyLinks] = useState({});
  const [savingLinks, setSavingLinks] = useState(false);
  const [productMap, setProductMap] = useState({});
  const [jvzooSecret, setJvzooSecret] = useState('');
  const [wplusSecret, setWplusSecret] = useState('');
  const [savingWh, setSavingWh] = useState(false);
  const [events, setEvents] = useState([]);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [loadError, setLoadError] = useState('');
  const load = useCallback(() => {
    setLoadError('');
    api.adminGetConfig().then((c) => { setCfg(c); setBuyLinks(c.buy_links || {}); setProductMap(c.product_map || {}); })
      .catch((e) => setLoadError(e.message || 'Could not load config'));
    api.adminLicenseEvents().then(setEvents).catch((e) => toast({ title: 'Could not load license events', description: e.message }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loadError) return <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">Could not load config: {loadError}</div>;
  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;

  const base = process.env.REACT_APP_BACKEND_URL || '';
  const jvzooUrl = `${base}/api/webhooks/jvzoo`;
  const wplusUrl = `${base}/api/webhooks/warriorplus?secret=${encodeURIComponent(cfg.wplus_secret || 'YOUR_SECRET')}`;
  const copyUrl = (u) => { navigator.clipboard.writeText(u); setCopiedUrl(u); setTimeout(() => setCopiedUrl(''), 1500); };
  const saveWebhooks = async () => {
    setSavingWh(true);
    const patch = { product_map: productMap };
    if (jvzooSecret.trim()) patch.jvzoo_secret = jvzooSecret.trim();
    if (wplusSecret.trim()) patch.wplus_secret = wplusSecret.trim();
    try { await api.adminSaveConfig(patch); toast({ title: 'Webhook settings saved' }); setJvzooSecret(''); setWplusSecret(''); load(); }
    catch (e) { toast({ title: 'Save failed', description: e.message }); }
    setSavingWh(false);
  };

  const saveLinks = async () => {
    setSavingLinks(true);
    try { await api.adminSaveConfig({ buy_links: buyLinks }); toast({ title: 'Buy links saved' }); load(); }
    catch (e) { toast({ title: 'Save failed', description: e.message }); }
    setSavingLinks(false);
  };

  const activeTiers = cfg.tiers || [];
  const toggleTier = async (t) => {
    const next = activeTiers.includes(t) ? activeTiers.filter((x) => x !== t) : [...activeTiers, t];
    try { await api.adminSaveConfig({ tiers: next, license_key: '' }); toast({ title: 'Modules updated' }); load(); }
    catch (e) { toast({ title: 'Update failed', description: e.message }); }
  };
  const applyLicense = async () => {
    if (!licenseKey.trim()) return;
    try { await api.adminSaveConfig({ license_key: licenseKey.trim() }); toast({ title: 'License applied' }); setLicenseKey(''); load(); }
    catch (e) { toast({ title: 'Invalid license', description: e.message }); }
  };
  const mint = async () => {
    try { const r = await api.adminMintLicense({ tiers: mintTiers, email: '' }); setMinted(r.license_key); }
    catch (e) { toast({ title: 'Mint failed', description: e.message }); }
  };
  const copyMint = () => { navigator.clipboard.writeText(minted); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-white">Active modules (OTOs)</h3>
        <p className="mb-3 text-xs text-white/50">Toggle to simulate what a buyer unlocks. In production this is set by the license key / funnel.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cfg.catalog.map((c) => {
            const on = activeTiers.includes(c.key);
            return (
              <button key={c.key} onClick={() => toggleTier(c.key)} data-testid={`tier-toggle-${c.key}`}
                className={`rounded-xl border p-4 text-left transition-colors ${on ? 'border-node-generator bg-node-generator/10' : 'border-[#2A2A30] bg-[#141417] hover:border-white/25'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white">{c.name}</span>
                  <span className={`h-4 w-4 rounded-full border ${on ? 'border-node-generator bg-node-generator' : 'border-white/30'}`}>{on && <Check size={14} className="text-black" />}</span>
                </div>
                <p className="mt-1 text-[11px] text-white/45">{c.tagline}</p>
                <p className="mt-2 text-[10px] text-white/30">{(cfg.all_features[c.key] || []).length} features</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
        <h3 className="text-sm font-semibold text-white">Apply a license key</h3>
        <p className="mb-3 text-xs text-white/50">Buyers paste the key they receive after purchase. It unlocks exactly their OTOs.</p>
        <div className="flex gap-2">
          <input value={licenseKey} onChange={(e) => setLicenseKey(e.target.value)} placeholder="IDOLARIS-…" data-testid="license-input" className={`${inputCls} flex-1`} />
          <button onClick={applyLicense} data-testid="license-apply" className="rounded-md bg-white px-4 py-2 text-sm font-medium text-black">Apply</button>
        </div>
      </div>

      <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
        <h3 className="text-sm font-semibold text-white">Generate a license key</h3>
        <p className="mb-3 text-xs text-white/50">Mint keys for buyers now (Phase 1). Later JVZoo/WarriorPlus webhooks will issue these automatically.</p>
        <div className="flex flex-wrap gap-2">
          {cfg.catalog.map((c) => (
            <button key={c.key} onClick={() => setMintTiers((p) => p.includes(c.key) ? p.filter((x) => x !== c.key) : [...p, c.key])}
              className={`rounded-full border px-3 py-1 text-xs ${mintTiers.includes(c.key) ? 'border-node-model bg-node-model/15 text-white' : 'border-[#2A2A30] text-white/60'}`}>
              {TIER_NAMES[c.key]}
            </button>
          ))}
          <button onClick={mint} data-testid="license-mint" className="rounded-md bg-node-model/20 px-4 py-1.5 text-sm font-medium text-node-model">Mint key</button>
        </div>
        {minted && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2">
            <code className="flex-1 truncate text-xs text-white/70">{minted}</code>
            <button onClick={copyMint} className="text-white/50 hover:text-white">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
        <h3 className="text-sm font-semibold text-white">Buy / affiliate links per product</h3>
        <p className="mb-3 text-xs text-white/50">Paste each product's sales page (with your JVZoo/WarriorPlus affiliate ID). These power the "Get / Upgrade" buttons on your landing &amp; pricing pages. A visitor's <code className="text-white/70">?ref=CODE</code> is appended automatically for sub-affiliate tracking.</p>
        <div className="space-y-2">
          {cfg.catalog.map((c) => (
            <div key={c.key} className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
              <span className="w-full shrink-0 text-xs text-white/70 sm:w-44">{c.name}</span>
              <input
                value={buyLinks[c.key] || ''}
                onChange={(e) => setBuyLinks((p) => ({ ...p, [c.key]: e.target.value }))}
                placeholder="https://warriorplus.com/o/…"
                data-testid={`buylink-${c.key}`}
                className={`${inputCls} flex-1`}
              />
            </div>
          ))}
        </div>
        <button onClick={saveLinks} disabled={savingLinks} data-testid="buylinks-save" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-medium text-black disabled:opacity-50">
          {savingLinks && <Loader2 size={14} className="animate-spin" />} Save buy links
        </button>
      </div>

      <OffersEditor />

      <div className="rounded-xl border border-node-generator/30 bg-node-generator/5 p-4">
        <h3 className="text-sm font-semibold text-white">Marketplace auto-unlock (JVZoo / WarriorPlus)</h3>
        <p className="mb-3 text-xs text-white/50">Paste these IPN/webhook URLs into your JVZoo & WarriorPlus product settings. When a buyer purchases (or refunds) a product, the matching module unlocks (or locks) automatically.</p>

        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2">
            <span className="w-24 shrink-0 text-[11px] uppercase tracking-wider text-white/40">JVZoo IPN</span>
            <code className="flex-1 truncate text-xs text-white/70" data-testid="webhook-jvzoo-url">{jvzooUrl}</code>
            <button onClick={() => copyUrl(jvzooUrl)} className="text-white/50 hover:text-white">{copiedUrl === jvzooUrl ? <Check size={14} /> : <Copy size={14} />}</button>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2">
            <span className="w-24 shrink-0 text-[11px] uppercase tracking-wider text-white/40">WarriorPlus</span>
            <code className="flex-1 truncate text-xs text-white/70" data-testid="webhook-wplus-url">{wplusUrl}</code>
            <button onClick={() => copyUrl(wplusUrl)} className="text-white/50 hover:text-white">{copiedUrl === wplusUrl ? <Check size={14} /> : <Copy size={14} />}</button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">JVZoo secret key {cfg.jvzoo_secret_masked && <span className="text-node-generator">· set</span>}</label>
            <input type="password" value={jvzooSecret} onChange={(e) => setJvzooSecret(e.target.value)} placeholder={cfg.jvzoo_secret_masked ? '•••• (stored)' : 'From JVZoo → Seller → My Account'} data-testid="jvzoo-secret" className={`${inputCls} mt-1 w-full`} />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">WarriorPlus secret {cfg.wplus_secret_masked && <span className="text-node-generator">· set</span>}</label>
            <input type="password" value={wplusSecret} onChange={(e) => setWplusSecret(e.target.value)} placeholder={cfg.wplus_secret_masked ? '•••• (stored)' : 'Any strong string you choose'} data-testid="wplus-secret" className={`${inputCls} mt-1 w-full`} />
          </div>
        </div>

        <p className="mb-2 mt-4 text-[11px] font-medium uppercase tracking-wider text-white/50">Map each product ID → module</p>
        <div className="space-y-2">
          {cfg.catalog.map((c) => (
            <div key={c.key} className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
              <span className="w-full shrink-0 text-xs text-white/70 sm:w-44">{c.name}</span>
              <input
                value={productMap[c.key] || ''}
                onChange={(e) => setProductMap((p) => ({ ...p, [c.key]: e.target.value }))}
                placeholder="Marketplace product ID (e.g. 99887)"
                data-testid={`productmap-${c.key}`}
                className={`${inputCls} flex-1`}
              />
            </div>
          ))}
        </div>
        <button onClick={saveWebhooks} disabled={savingWh} data-testid="webhooks-save" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-medium text-black disabled:opacity-50">
          {savingWh && <Loader2 size={14} className="animate-spin" />} Save webhook settings
        </button>

        {events.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-white/50">Recent unlock events</p>
            <div className="space-y-1">
              {events.slice(0, 6).map((ev) => (
                <div key={ev.id} className="flex items-center justify-between rounded-md border border-[#2A2A30] bg-[#0E0E10] px-2.5 py-1.5 text-xs">
                  <span className="text-white/70"><span className="uppercase text-white/40">{ev.source}</span> · {TIER_NAMES[ev.tier] || ev.tier || '-'}</span>
                  <span className={ev.action === 'unlock' ? 'text-node-generator' : ev.action === 'lock' ? 'text-red-300' : 'text-white/40'}>{ev.action}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const CAP_LABELS = {
  video: { title: 'Video', help: 'Motion clips from a still frame. Whichever provider sits first with a key is the one billed.' },
  image: { title: 'Image', help: 'Product imagery and asset analysis.' },
  text: { title: 'Text', help: 'Motion scripts, voice-over drafts and prompt writing. Cheapest capability to run, so a dedicated key here pays for itself.' },
  avatar_video: { title: 'Avatar Video', help: 'A talking clip driven by an audio track, lip-synced to an optional reference photo. Separate from Video above -- nothing in that chain takes audio as input.' },
};

function ProviderRow({ row, index, total, capKey, onMove, onToggle, keyValue, setKeyValue }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const active = index === 0 && row.enabled && row.has_key;

  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await api.adminTestKey(row.id);
      setResult(r);
      toast({ title: r.ok ? `${row.label} works` : `${row.label} rejected`, description: r.message });
    } catch (e) {
      setResult({ ok: false, message: e.message });
      toast({ title: 'Test failed', description: e.message });
    } finally { setTesting(false); }
  };

  return (
    <div data-testid={`chain-${capKey}-${row.id}`}
      className={`rounded-lg border p-3 transition-colors ${active ? 'border-node-generator/40 bg-node-generator/5' : 'border-[#2A2A30] bg-[#141417]'}`}>
      <div className="flex items-center gap-2">
        <span className="w-4 shrink-0 text-center text-[11px] font-semibold text-white/30">{index + 1}</span>
        <div className="flex shrink-0 flex-col">
          <button onClick={() => onMove(index, -1)} disabled={index === 0} data-testid={`chain-up-${capKey}-${row.id}`}
            title="Move up" className="text-white/40 transition-colors hover:text-white disabled:opacity-20"><ChevronUp size={13} /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1} data-testid={`chain-down-${capKey}-${row.id}`}
            title="Move down" className="text-white/40 transition-colors hover:text-white disabled:opacity-20"><ChevronDown size={13} /></button>
        </div>
        <span className="text-sm font-medium text-white">{row.label}</span>
        {active && <span className="rounded-full bg-node-generator/15 px-2 py-0.5 text-[10px] font-semibold text-node-generator">in use</span>}
        <span className={`rounded-full px-2 py-0.5 text-[10px] ${row.has_key ? 'bg-white/10 text-white/60' : 'bg-red-500/15 text-red-300'}`}>
          {row.has_key ? 'key stored' : 'no key'}
        </span>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-white/50">
          <input type="checkbox" checked={row.enabled} onChange={() => onToggle(row.id)}
            data-testid={`chain-enable-${capKey}-${row.id}`} className="accent-white" />
          enabled
        </label>
      </div>

      <p className="ml-6 mt-1 text-[11px] leading-relaxed text-white/40">{row.note}</p>

      <div className="ml-6 mt-2 flex flex-wrap items-center gap-2">
        <input type="password" value={keyValue} onChange={(e) => setKeyValue(row.id, e.target.value)}
          placeholder={row.has_key ? 'Stored. Paste a new key to replace' : 'Paste key'}
          data-testid={`chain-key-${row.id}`}
          className={`${inputCls} min-w-[220px] flex-1`} />
        <button type="button" onClick={runTest} disabled={testing || !row.has_key} data-testid={`chain-test-${row.id}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#2A2A30] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-40">
          {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
          {testing ? 'Testing' : 'Test'}
        </button>
        <a href={row.console_url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-node-generator hover:underline">
          Get key <ExternalLink className="h-3 w-3" />
        </a>
        {result && (
          <span className={`text-xs ${result.ok ? 'text-node-generator' : 'text-red-300'}`}>
            {result.ok ? '✓' : '✗'} {result.message}
          </span>
        )}
      </div>
    </div>
  );
}

// Video ships off, and it is one boolean on the server. Until this switch
// existed the only way to move it was a POST by hand, which put the feature out
// of reach of the person who actually owns the instance.
function VideoSwitch({ on, setOn }) {
  return (
    <div data-testid="video-enabled-panel"
      className={`mb-2 rounded-lg border p-3 ${on ? 'border-node-generator/40 bg-node-generator/5' : 'border-[#2A2A30] bg-[#141417]'}`}>
      <label className="flex cursor-pointer items-center gap-2">
        <input type="checkbox" checked={on} onChange={() => setOn(!on)}
          data-testid="video-enabled" className="accent-white" />
        <span className="text-sm font-medium text-white">Offer video in the studio</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] ${on ? 'bg-node-generator/15 text-node-generator' : 'bg-white/10 text-white/60'}`}>
          {on ? 'on' : 'off'}
        </span>
      </label>
      <p className="ml-6 mt-1 text-[11px] leading-relaxed text-white/40">
        Off hides the video button everywhere and answers the video endpoints with a short
        explanation rather than a failure. Turn it on once a provider below has a key that
        passes its test and has rendered a clip: buyers see the button the moment this saves,
        and a clip that fails costs them the wait and you the credit. Saved with Save providers.
      </p>
    </div>
  );
}

function KeysTab() {
  const [cfg, setCfg] = useState(null);
  const [chain, setChain] = useState({});
  const [disabled, setDisabled] = useState([]);
  const [keys, setKeys] = useState({});
  const [videoOn, setVideoOn] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(() => {
    setLoadError('');
    api.adminGetConfig().then((c) => {
      setCfg(c);
      setVideoOn(!!c.video_enabled);
      const prov = c.providers || {};
      const next = {};
      Object.keys(prov).forEach((cap) => { next[cap] = prov[cap].providers.map((r) => r.id); });
      setChain(next);
      setDisabled(Object.values(prov).flatMap((v) => v.providers.filter((r) => !r.enabled).map((r) => r.id)));
      setKeys({});
    }).catch((e) => setLoadError(e.message || 'Could not load providers'));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loadError) return <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">Could not load providers: {loadError}</div>;
  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;

  const prov = cfg.providers || {};

  const move = (cap) => (index, delta) => {
    setChain((prev) => {
      const order = [...(prev[cap] || [])];
      const to = index + delta;
      if (to < 0 || to >= order.length) return prev;
      [order[index], order[to]] = [order[to], order[index]];
      return { ...prev, [cap]: order };
    });
  };

  const toggle = (id) => setDisabled((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  const setKeyValue = (id, val) => setKeys((prev) => ({ ...prev, [id]: val }));

  const save = async () => {
    const provider_keys = {};
    Object.entries(keys).forEach(([id, v]) => { if (v && v.trim()) provider_keys[id] = v.trim(); });
    try {
      await api.adminSaveConfig({ provider_chain: chain, provider_disabled: disabled, provider_keys,
                                  video_enabled: videoOn });
      toast({ title: 'Providers saved' });
      load();
    } catch (e) { toast({ title: 'Save failed', description: e.message }); }
  };

  // The saved order is authoritative, but the rows arrive in the order the API
  // last stored, so they are re-sorted against local state to keep the arrows
  // responsive before a save.
  const rowsFor = (cap) => {
    const byId = Object.fromEntries((prov[cap]?.providers || []).map((r) => [r.id, r]));
    return (chain[cap] || []).map((id) => byId[id]).filter(Boolean)
      .map((r) => ({ ...r, enabled: !disabled.includes(r.id) }));
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-node-generator/30 bg-node-generator/5 p-4 text-sm text-white/70">
        <strong className="text-white">Bring your own keys: you control your AI costs.</strong> Idolaris never sells
        tokens. Providers are tried top to bottom and the first one holding a key does the work, so put the account you
        want billed at the top. The rest stay as cover for when that one runs out of credit or goes down.
      </div>

      {Object.keys(CAP_LABELS).filter((cap) => prov[cap]).map((cap) => (
        <div key={cap}>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="text-sm font-semibold text-white">{CAP_LABELS[cap].title}</h3>
            <span className="text-[11px] text-white/40">
              {prov[cap].active ? `using ${prov[cap].providers.find((r) => r.id === prov[cap].active)?.label}` : 'no provider ready'}
            </span>
          </div>
          <p className="mb-2 text-[11px] text-white/40">{CAP_LABELS[cap].help}</p>
          {cap === 'video' && <VideoSwitch on={videoOn} setOn={setVideoOn} />}
          <div className="space-y-2">
            {rowsFor(cap).map((row, i, arr) => (
              <ProviderRow key={row.id} row={row} index={i} total={arr.length} capKey={cap}
                onMove={move(cap)} onToggle={toggle}
                keyValue={keys[row.id] || ''} setKeyValue={setKeyValue} />
            ))}
          </div>
        </div>
      ))}

      <button onClick={save} data-testid="keys-save" className="rounded-md bg-white px-5 py-2 text-sm font-medium text-black">Save providers</button>
    </div>
  );
}

// Estos cinco datos estaban escritos a mano dentro de Legal.jsx, como
// [registered address]. Nadie que venda esto edita un JSX, asi que en la
// practica salia a la venta con corchetes en los terminos -- y una pasarela de
// pago revisa esa pagina antes de aprobar a un vendedor.
const LEGAL_FIELDS = [
  ['company', 'Registered company name', 'Eager Dragon Publishing Ltd'],
  ['company_number', 'Company number', '12345678'],
  ['jurisdiction', 'Jurisdiction', 'England and Wales'],
  ['address', 'Registered address', '1 Example Street, London, N1 1AA'],
  ['support_email', 'Support email', 'hello@example.com'],
];

function LegalTab() {
  const [legal, setLegal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    api.adminGetConfig().then((c) => setLegal({
      ...Object.fromEntries(LEGAL_FIELDS.map(([k]) => [k, ''])), ...(c.legal || {}),
    })).catch((e) => setLoadError(e.message || 'Could not load legal details'));
  }, []);
  if (loadError) return <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">Could not load legal details: {loadError}</div>;
  if (!legal) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.adminSaveConfig({ legal });
      toast({ title: 'Legal details saved',
              description: 'They appear in Terms, Privacy, Refunds and Licence straight away.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message });
    } finally { setSaving(false); }
  };

  const missing = LEGAL_FIELDS.filter(([k]) => !(legal[k] || '').trim()).length;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4 text-sm leading-relaxed text-white/70">
        <strong className="text-white">Who is selling this.</strong> These go into the
        Terms, Privacy, Refunds and Licence pages. Until they are filled in those
        documents show the field name in brackets, which is what a payment processor
        looks at before approving a seller. Nothing is invented for you: an address
        that does not exist is worse than a gap, because it misleads whoever reads it.
      </div>

      <div className="space-y-3 rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
        {LEGAL_FIELDS.map(([k, label, example]) => (
          <label key={k} className="block">
            <span className="text-xs text-white/50">{label}</span>
            <input
              value={legal[k] || ''}
              onChange={(e) => setLegal({ ...legal, [k]: e.target.value })}
              placeholder={example}
              data-testid={`legal-${k}`}
              className={`mt-1 w-full ${inputCls} ${(legal[k] || '').trim() ? '' : 'border-amber-500/40'}`}
            />
          </label>
        ))}
        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={saving} data-testid="legal-save"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
          <span className="text-xs text-white/40">
            {missing ? `${missing} still empty` : 'All filled in'}
          </span>
          <a href="/legal/terms" target="_blank" rel="noreferrer"
             className="ml-auto inline-flex items-center gap-1 text-xs text-white/50 hover:text-white">
            See how they read <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function BrandingTab() {
  const { has } = useEntitlements();
  const [cfg, setCfg] = useState(null);
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [accent, setAccent] = useState('#22D3EE');
  const [logo, setLogo] = useState('');
  const [loadError, setLoadError] = useState('');
  const load = useCallback(() => { setLoadError(''); api.adminGetConfig().then((c) => {
    setCfg(c); const b = c.branding || {}; setName(b.name || 'Idolaris'); setTagline(b.tagline || ''); setAccent(b.accent || '#22D3EE'); setLogo(b.logo_url || '');
  }).catch((e) => setLoadError(e.message || 'Could not load branding')); }, []);
  useEffect(() => { load(); }, [load]);
  if (loadError) return <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-300">Could not load branding: {loadError}</div>;
  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" /></div>;

  if (!has('whitelabel.branding')) {
    return (
      <div className="rounded-2xl border border-dashed border-[#2A2A30] p-12 text-center" data-testid="branding-locked">
        <Lock size={26} className="mx-auto text-white/30" />
        <p className="mt-3 font-medium text-white/80">Whitelabel branding is locked</p>
        <p className="mt-1 text-sm text-node-model">🔒 Unlock with {TIER_NAMES.oto4} to rebrand the platform with your own name, logo & colors.</p>
      </div>
    );
  }

  const uploadLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    (async () => {
      try { const dataUrl = await downscaleToDataUrl(f, 512); const a = await api.createAsset({ type: 'branding', name: f.name, dataUrl }); setLogo(a.url); }
      catch (err) { toast({ title: 'Logo upload failed', description: err.message }); }
    })();
  };
  const save = async () => {
    try { await api.adminSaveConfig({ branding: { name: name.trim() || 'Idolaris', tagline, accent, logo_url: logo } }); toast({ title: 'Branding saved', description: 'Reload to see it applied everywhere.' }); }
    catch (e) { toast({ title: 'Save failed', description: e.message }); }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-node-model/30 bg-node-model/5 p-4 text-sm text-white/70">
        <strong className="text-white">Whitelabel.</strong> Make IDOLARIS your own: set the product name, logo and accent color your buyers/clients will see.
      </div>
      <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-4">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-[#2A2A30] bg-[#0E0E10]">
            {logo ? <img src={logo} alt="" className="h-full w-full object-contain" /> : <Paintbrush size={20} className="text-white/30" />}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-[#2A2A30] px-3 py-2 text-sm text-white/80 hover:border-white/30">
            <Upload size={14} /> Upload logo
            <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="branding-logo" />
          </label>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Product name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="branding-name" className={`${inputCls} mt-1`} />
      </div>
      <div>
        <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Tagline</label>
        <input value={tagline} onChange={(e) => setTagline(e.target.value)} data-testid="branding-tagline" className={`${inputCls} mt-1`} placeholder="Create AI influencers that sell" />
      </div>
      <div>
        <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Accent color</label>
        <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="mt-1 h-9 w-24 cursor-pointer rounded-md border border-[#2A2A30] bg-[#141417]" />
      </div>
      <button onClick={save} data-testid="branding-save" className="rounded-md bg-white px-5 py-2 text-sm font-medium text-black">Save branding</button>
    </div>
  );
}

// Lo que impide vender esto, dicho antes de que lo descubra un cliente. Se lee
// del estado real -- que enlaces de pago hay puestos, que ids de producto estan
// mapeados, si los terminos siguen con corchetes -- y no de una lista escrita a
// mano, que es lo unico que sobrevive a un mes de cambios.
function Readiness() {
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => { api.adminReadiness().then(setR).catch((e) => toast({ title: 'Could not load readiness checks', description: e.message })); }, []);
  if (!r) return null;

  const bad = (r.checks || []).filter((c) => !c.ok);
  if (r.ready && bad.length === 0) {
    return (
      <div className="mt-6 flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-200/90">
        <ShieldCheck size={15} /> Everything needed to take a payment is configured.
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="admin-readiness">
      <button onClick={() => setOpen(!open)}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-amber-200">
        <ShieldCheck size={15} className="shrink-0" />
        <span className="font-medium">
          {r.blocking > 0
            ? `${r.blocking} thing${r.blocking === 1 ? '' : 's'} still stop you taking a payment`
            : 'Ready to sell, with a few loose ends'}
        </span>
        <span className="ml-auto text-xs text-amber-200/60">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <ul className="space-y-3 border-t border-amber-500/20 px-4 py-3">
          {bad.map((c) => (
            <li key={c.key} className="text-sm">
              <p className="flex items-center gap-2 text-white/85">
                <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${
                  c.blocking ? 'bg-amber-400' : 'bg-white/30'}`} />
                {c.label}
                {!c.blocking && <span className="text-[10px] text-white/30">optional</span>}
              </p>
              <p className="mt-0.5 pl-3.5 text-xs leading-relaxed text-white/45">{c.why}</p>
              <p className="mt-0.5 pl-3.5 text-xs leading-relaxed text-amber-200/70">{c.fix}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('overview');
  const brand = useBranding();

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <header className="flex h-16 items-center justify-between border-b border-[#2A2A30] px-6">
        <Link to="/" className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none">
            <circle cx="5" cy="6" r="2.4" fill="#F5A623" /><circle cx="5" cy="18" r="2.4" fill="#A78BFA" />
            <circle cx="19" cy="12" r="2.4" fill="#fff" /><path d="M7 6.6 17 11.4M7 17.4 17 12.6" stroke="#3A3A42" strokeWidth="1.3" />
          </svg>
          <span className="font-serif-display text-xl">{brand.name}</span>
          <span className="ml-1 rounded-full bg-node-model/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-node-model">Admin</span>
        </Link>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/app')} className="text-sm text-white/60 hover:text-white">Studio</button>
          <span className="hidden text-sm text-white/60 sm:inline">{user?.name}</span>
          <button onClick={() => { logout(); navigate('/'); }} className="flex h-8 w-8 items-center justify-center rounded-md text-white/50 hover:bg-white/5 hover:text-white"><LogOut size={15} /></button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="font-serif-display text-3xl">Admin control room</h1>
        <p className="mt-1 text-sm text-white/50">Manage users, curate the gallery, edit style templates and watch the numbers.</p>

        <div className="mt-6 flex gap-2 border-b border-[#2A2A30]">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-3 text-sm transition-colors ${tab === t.key ? 'border-white text-white' : 'border-transparent text-white/50 hover:text-white'}`}>
              <t.Icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {/* Encima de las pestanas y no dentro de una: lo que falta para poder
            cobrar no deberia haber que ir a buscarlo. Desaparece solo cuando no
            queda nada bloqueando. */}
        <Readiness />

        <div className="mt-8">
          {tab === 'overview' && <Overview />}
          {tab === 'sales' && <SalesTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'license' && <LicenseTab />}
          {tab === 'keys' && <KeysTab />}
          {tab === 'branding' && <BrandingTab />}
          {tab === 'legal' && <LegalTab />}
          {tab === 'gallery' && <GalleryTab />}
          {tab === 'styles' && <StylesTab />}
        </div>
      </main>
    </div>
  );
}
